import { useContext, useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { Logo, SelectArrow } from "@assets/icons";
import { BaseLink } from "@components/Form";
import {
  SETTINGS,
  APPOINTMENTS,
  CONFIG,
  CUSTOM_REPORTS,
  PROCESS,
} from "@router/consts";
import { sendNotify } from "@components/GlobalNotify/GlobalNotify";
import { Divider, Popup } from "@components/Static";
import { UserContext } from "@contexts/User";
import { useProfileImageUrl } from "@hooks/profile";
import { useFetchHook } from "@hooks/useFetchHook";
import ProfilePicture from "@components/ProfilePicture";
import NavPopupButton from "@components/Static/Popup/NavPopupButton";
import NavbarList from "@components/Navbar/NavbarList";
import {
  USER_ROLES,
  USER_STATUSES,
  CUSTOM_USER_ROLES,
  MEDIA_SCREEN,
} from "@utils/consts";
import Loader from "@components/Loader";
import AccountGraphqlInstance from "@services/api/account.graphql";
import styles from "./Navbar.module.scss";
import distributedUserInstance from "@services/api/distributedUser.graphql";
import { ability, convertAbility } from "ability/ability";
import RolesGraphqlInstance from "@services/api/roles.graphql";
import { useUnansweredEvent } from "@hooks/appointment";
import { AppointmentReportContext } from "@contexts/AppointmentReport";
import { isExactMatchArray } from "@utils/helpers";
import accountPersonService from "@services/api/accountPerson.graphql";
import { useLocation } from "react-router-dom";
import NavigationBlockerDialog from "@components/NavigationBlockerDialog";
import { useNotifications } from "@hooks/notifications";
import useResize from "@hooks/useResize";
import NavbarProfileMenu from "./NavbarProfileMenu";
import { PROFILE_GUIDE_STEPS } from "@components/NavigationGuide/steps";

const Navbar = ({ className, navItems }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const {
    logout,
    profile,
    updateCurrentProfile,
    htmlFontSize,
    user,
    switchAccountTokens,
    isAllAccountsLogin,
  } = useContext(UserContext);
  const [sendFetch] = useFetchHook(updateCurrentProfile);
  const [showNavigationConfirmDialog, setShowNavigationConfirmDialog] =
    useState({
      isOpen: false,
      ids: null,
      text: null,
    });
  const profilePictureUrl = useProfileImageUrl(
    profile?.profilePictureName,
    profile?.accountId,
  );

  const [isDistributedLoading, setIsDistributedLoading] = useState(false);
  const [distributedAccounts, setDistributedAccounts] = useState([]);
  const [isSwitchInProgress, setIsSwitchInProgress] = useState(false);
  const [usersAccounts, setUsersAccounts] = useState(null);
  const isAllbryCounsellor = user?.roles?.some(
    (role) => role.name === USER_ROLES.ALLBRY_COUNSELLOR_ROLE,
  );
  const isCounsellor = user?.roles?.some(
    (role) => role.name === USER_ROLES.COUNSELLOR_ROLE,
  );
  const isStudent = user?.roles?.some(
    (role) => role.name === USER_ROLES.STUDENT_ROLE,
  );
  const showNotifications = isCounsellor || isAllbryCounsellor;
  const canViewUnansweredEvents =
    isStudent || isCounsellor || isAllbryCounsellor;
  const [unansweredEvent, fetchData, setDistributedUsers] = useUnansweredEvent({
    forAllAccount: true,
    enabled: canViewUnansweredEvents,
  });
  const { appointmentSocket } = useContext(AppointmentReportContext);
  const currentAccountUnansweredEvent = useMemo(
    () =>
      (unansweredEvent || []).filter((e) => e.accountId === profile?.accountId),
    [unansweredEvent, profile?.accountId],
  );

  useEffect(() => {
    if (!appointmentSocket || appointmentSocket.disconnected) {
      return undefined;
    }
    appointmentSocket.on("appointment", fetchData);
    return () => {
      appointmentSocket.off("appointment", fetchData);
    };
  }, [appointmentSocket?.connected]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [isOnline, setIsOnline] = useState(profile?.isOnline);
  const now = Date.now();
  const { unreadCount } = useNotifications(showNotifications);
  const { windowWidth } = useResize();
  // Students only: below this width the navbar is rendered as a bottom tab bar, so popups open upwards
  const isMobileNav =
    isStudent && windowWidth > 0 && windowWidth < MEDIA_SCREEN.MOBILE;
  // Students only, bottom tab bar: slightly larger text in the profile menu
  const mobileMenuTextClass = isMobileNav ? "text-[0.9375rem]" : undefined;
  const isInConfigCreateRoute = (pathname) =>
    pathname?.startsWith(`${CONFIG}/create/`) ||
    pathname?.startsWith(`${CUSTOM_REPORTS}/create/`) ||
    pathname?.startsWith(`${CONFIG}/createOwnTemplate`) ||
    pathname?.startsWith(`${CUSTOM_REPORTS}/createTemplate`) ||
    pathname?.startsWith(`${PROCESS}/create/`);
  const isConfigCreateRoute = isInConfigCreateRoute(location.pathname);

  useEffect(() => {
    if (user?.accountIds) {
      setSelectedAccountId(user.accountIds);
    }
  }, [user?.accountIds]);

  // Load accounts for regular users
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (isAllbryCounsellor) return;
      setIsLoading(true);
      try {
        const usersResponse = await AccountGraphqlInstance.getAccounts();
        if (cancelled) return;
        const activeAccounts = usersResponse.filter(
          (a) =>
            now >= Number(a.account.dtActivation) &&
            now < Number(a.account.dtDeactivation),
        );
        setUsersAccounts(activeAccounts);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isCounsellor, location]);

  // Load distributed accounts for Allbry counsellors
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isAllbryCounsellor) return;
      setIsDistributedLoading(true);
      try {
        const distributedUsersResponse =
          await distributedUserInstance.getAllAuthDistributedUserByUserId();
        if (cancelled) return;
        const activeDistributed = [];
        const activeIds = [];
        for (const du of distributedUsersResponse) {
          if (du.status !== USER_STATUSES.ACTIVE) continue;
          const anyActive = (du.distributedUser?.accounts || []).some(
            (account) =>
              (now >= Number(account.dtActivation) &&
                now < Number(account.dtDeactivation)) ||
              account.id === null,
          );
          if (anyActive) {
            activeDistributed.push(du);
            if (du.distributedUserId) activeIds.push(du.distributedUserId);
          }
        }
        setDistributedAccounts(activeDistributed);
        setDistributedUsers(activeIds);
      } finally {
        if (!cancelled) setIsDistributedLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAllbryCounsellor, location]);

  /**
   * Returns true if a switch was triggered.
   */
  const handleAutoSwitch = (ids, switchFn) => {
    if (!ids || !Array.isArray(ids) || ids.length === 0) return false;
    const shouldSwitch =
      isAllAccountsLogin && !isExactMatchArray(ids, selectedAccountId);
    if (shouldSwitch) {
      switchFn(ids);
      return true;
    }
    return false;
  };

  /**
   * Auto-switch based on loaded account lists (no popup action required).
   * Listens to usersAccounts / distributedAccounts so it runs when API data arrives.
   * Guarded by isSwitchInProgress to avoid loops.
   */
  useEffect(() => {
    if (!user || isSwitchInProgress) return;
    if (isAllbryCounsellor) {
      const firstActive = distributedAccounts?.[0];
      const ids = firstActive?.distributedAccountIds;
      if (!isExactMatchArray(ids, selectedAccountId)) {
        if (ids) handleAutoSwitch(ids, onSwitchAccount);
      }
    } else {
      const firstActive = usersAccounts?.[0];
      const ids = firstActive?.account?.ids;
      if (!isExactMatchArray(ids, selectedAccountId)) {
        if (ids) handleAutoSwitch(ids, handleAccountSwitch);
      }
    }
    // Note: do NOT include selectedAccountId here; switching updates it and can cause loops before redirect.
  }, [usersAccounts, distributedAccounts]);

  const handleUserOpen = async () => {
    const usersResponse = await AccountGraphqlInstance.getAccounts();
    const activeAccounts = usersResponse.filter(
      (a) =>
        now >= Number(a.account.dtActivation) &&
        now < Number(a.account.dtDeactivation),
    );

    // Auto-switch if needed
    handleAutoSwitch(activeAccounts?.[0]?.account?.ids, handleAccountSwitch);

    setUsersAccounts(activeAccounts);
    fetchData();
  };

  const handleDistributedOpen = async () => {
    const distributedUsersResponse =
      await distributedUserInstance.getAllAuthDistributedUserByUserId();
    const accounts = [];
    for (const allAccounts of distributedUsersResponse) {
      if (allAccounts.status !== USER_STATUSES.ACTIVE) continue;
      const anyActive = (allAccounts.distributedUser?.accounts || []).some(
        (account) =>
          (now >= Number(account.dtActivation) &&
            now < Number(account.dtDeactivation)) ||
          account.id === null,
      );
      if (anyActive) accounts.push(allAccounts);
    }

    // Auto-switch if needed
    handleAutoSwitch(accounts?.[0]?.distributedAccountIds, onSwitchAccount);

    setDistributedAccounts(accounts);
    fetchData();
  };

  if (!profile) {
    return (
      <div className={clsx(styles.nav, "justify-center")}>
        <Loader height={60} width={60} />
      </div>
    );
  }

  const PopupTrigger = (__open) => (
    <div
      className={clsx(
        "cursor-pointer",
        isStudent && clsx("group", styles.studentAvatarTrigger),
        styles.profileTrigger,
      )}
      data-navigation-guide={PROFILE_GUIDE_STEPS.MENU}
    >
      <ProfilePicture
        profilePictureUrl={profilePictureUrl}
        profile={{ ...profile, isOnline }}
        size={48}
        sizeClassName={clsx(
          "w-9 h-9 lg:w-10 lg:h-10 xl:w-12 xl:h-12",
          isStudent && styles.studentAvatar,
        )}
        statusSize={isMobileNav ? 7 : 10}
        isShowOnline
        alt="profile"
      />
      {/* Students only, bottom tab bar: label under the avatar, styled like the other tabs */}
      {isStudent && (
        <span
          className={clsx(
            "md:hidden group-hover:text-opacity-100 font-medium-poppins capitalize transition text-opacity-80 text-sonic-silver",
            styles.navItemText,
          )}
        >
          {t("navbar.profile")}
        </span>
      )}
    </div>
  );

  const SwitchAccountTrigger = (__open) => (
    <div
      className={clsx(
        "flex rounded-md items-center relative justify-center mx-3 py-1 cursor-pointer",
        isStudent ? "md:mb-2" : "mb-2",
        __open && "bg-opacity-70 bg-pampas",
      )}
    >
      <Logo size="h-10 w-10" />
      <div className="absolute right-1 my-auto">
        <SelectArrow />
      </div>
    </div>
  );

  const onSwitchAccount = async (ids) => {
    // ids: array for distributed accounts
    if (isSwitchInProgress) return;
    try {
      setIsSwitchInProgress(true);
      // Keep local state in sync to avoid re-triggers before redirect
      setSelectedAccountId(ids);
      await switchAccountTokens(ids);
      try {
        const data = await RolesGraphqlInstance.getRoleFeaturesByCurrentUser();
        if (data) {
          const rules = convertAbility(data || []);
          ability.update(rules);
        }
      } finally {
        window.location.replace(getRedirectUrl());
      }
    } catch (e) {
      setIsSwitchInProgress(false);
      sendNotify(t("common.switchAccountError"), "error");
    }
  };

  const handleOnline = async (updatedIsOnline) => {
    if (isLoading) return;
    await accountPersonService
      .updateOnlineStatus(updatedIsOnline)
      .then(() => {
        sendNotify(t("settings.onlineStatus.updated"), "success");
        setIsOnline(updatedIsOnline);
      })
      .catch(() => {
        sendNotify(t("settings.onlineStatus.error"), "error");
      });
  };

  const AllbryIcon = () => <Logo size="h-10 w-10" />;

  const calculateMessageCounts = (messages, accountId, isGlobal) => {
    return (messages || []).reduce(
      (counts, msg) => {
        if (isGlobal) {
          counts.sendAndReadMessageCount +=
            Number(msg.sendMessageCount || 0) +
            Number(msg.readMessageCount || 0);
          counts.allMessageCount += Number(
            msg.globalChatRoom?.messageCount || 0,
          );
        } else {
          if (msg.accountId === accountId || accountId === null) {
            counts.sendAndReadMessageCount +=
              Number(msg.sendMessageCount || 0) +
              Number(msg.readMessageCount || 0);
            counts.allMessageCount += Number(msg.chatRoom?.messageCount || 0);
          }
        }
        return counts;
      },
      { sendAndReadMessageCount: 0, allMessageCount: 0 },
    );
  };

  const calculateUnreadMessageCount = (
    allMessageCount,
    sendAndReadMessageCount,
    accountId,
  ) => {
    return (
      (allMessageCount || 0) -
      (sendAndReadMessageCount || 0) +
      (unansweredEvent || []).filter((e) => e.accountId === accountId).length
    );
  };

  const renderAllbryAccounts = useMemo(() => {
    if (
      isDistributedLoading ||
      !distributedAccounts.length ||
      isSwitchInProgress
    ) {
      return (
        <div className="h-20 flex items-center">
          <Loader height={40} width={40} />
        </div>
      );
    }

    return distributedAccounts.map((account) => {
      const { sendAndReadMessageCount, allMessageCount } =
        calculateMessageCounts(
          account.distributedUser?.chatChatParticipants || [],
          account.distributedAccountId,
          false,
        );
      const {
        sendAndReadMessageCount: sendAndReadMessageCountGlobal,
        allMessageCount: allMessageCountGlobal,
      } = calculateMessageCounts(
        account.distributedUser?.chatChatParticipantGlobals || [],
        account.distributedAccountId,
        true,
      );

      const unreadMessageCount = calculateUnreadMessageCount(
        allMessageCount,
        sendAndReadMessageCount,
        account.distributedAccountId,
      );
      const unreadMessageCountGlobal = calculateUnreadMessageCount(
        allMessageCountGlobal,
        sendAndReadMessageCountGlobal,
        account.distributedAccountId,
      );
      const totalUnreadMessageCount = isAllAccountsLogin
        ? unreadMessageCount
        : unreadMessageCount + unreadMessageCountGlobal;

      return (
        <NavPopupButton
          key={
            account.distributedAccountId ||
            JSON.stringify(account.distributedAccountIds)
          }
          Icon={AllbryIcon}
          text={(account.distributedUser?.accounts || []).map((n) => n.name)}
          className={
            isExactMatchArray(account.distributedAccountIds, selectedAccountId)
              ? "bg-pampas"
              : undefined
          }
          unreadMessage={
            totalUnreadMessageCount > 0 ? totalUnreadMessageCount : ""
          }
          textClassName="max-w-30"
          onClick={() =>
            isConfigCreateRoute
              ? setShowNavigationConfirmDialog({
                  isOpen: true,
                  ids: account.distributedAccountIds,
                  text: (account.distributedUser?.accounts || []).map(
                    (n) => n.name,
                  ),
                })
              : onSwitchAccount(account.distributedAccountIds)
          }
          isSelected={isExactMatchArray(
            account.distributedAccountIds,
            selectedAccountId,
          )}
        />
      );
    });
  }, [
    isDistributedLoading,
    distributedAccounts,
    isSwitchInProgress,
    unansweredEvent,
    selectedAccountId,
  ]);

  const renderUsersAccounts = useMemo(() => {
    if (!usersAccounts || !usersAccounts.length || isSwitchInProgress) {
      return (
        <div className="h-20 flex items-center">
          <Loader height={40} width={40} />
        </div>
      );
    }

    return usersAccounts.map((usersAccount) => {
      const { sendAndReadMessageCount, allMessageCount } =
        calculateMessageCounts(
          usersAccount.user?.chatChatParticipants || [],
          usersAccount.account?.id,
          false,
        );
      const {
        sendAndReadMessageCount: sendAndReadMessageCountGlobal,
        allMessageCount: allMessageCountGlobal,
      } = calculateMessageCounts(
        usersAccount.user?.chatChatParticipantGlobals || [],
        usersAccount.account?.id,
        true,
      );

      const unreadMessage = calculateUnreadMessageCount(
        allMessageCount,
        sendAndReadMessageCount,
        usersAccount.account?.id,
      );
      const unreadMessageGlobal = calculateUnreadMessageCount(
        allMessageCountGlobal,
        sendAndReadMessageCountGlobal,
        usersAccount.account?.id,
      );
      const unreadMessageCount = isAllAccountsLogin
        ? unreadMessage || 0
        : (unreadMessage || 0) + (unreadMessageGlobal || 0);
      return (
        <NavPopupButton
          key={usersAccount.account?.id}
          Icon={AllbryIcon}
          text={usersAccount.account?.name}
          unreadMessage={unreadMessageCount > 0 ? unreadMessageCount : ""}
          className={
            isExactMatchArray(usersAccount.account?.ids, selectedAccountId)
              ? "bg-pampas"
              : undefined
          }
          textClassName="max-w-30"
          onClick={() =>
            isConfigCreateRoute
              ? setShowNavigationConfirmDialog({
                  isOpen: true,
                  ids: usersAccount.account?.ids,
                  text: usersAccount.account?.name,
                })
              : handleAccountSwitch(usersAccount.account?.ids)
          }
          isSelected={isExactMatchArray(
            usersAccount.account?.ids,
            selectedAccountId,
          )}
        />
      );
    });
  }, [usersAccounts, isSwitchInProgress, unansweredEvent, selectedAccountId]);

  const getRedirectUrl = () => {
    return ability.can(
      CUSTOM_USER_ROLES[USER_ROLES.COUNSELLOR_ROLE].APPOINTMENTS,
      USER_ROLES.COUNSELLOR_ROLE,
    ) ||
      ability.can(
        CUSTOM_USER_ROLES[USER_ROLES.STUDENT_ROLE].APPOINTMENTS,
        USER_ROLES.STUDENT_ROLE,
      )
      ? APPOINTMENTS.toString()
      : SETTINGS;
  };

  const handleAccountSwitch = (accountIds) => {
    if (isSwitchInProgress) return;
    // Keep local state in sync to avoid re-triggers before redirect
    setIsSwitchInProgress(true);
    setSelectedAccountId(accountIds);
    AccountGraphqlInstance.updateAuthUserSession(accountIds)
      .then(async () => {
        try {
          const data =
            await RolesGraphqlInstance.getRoleFeaturesByCurrentUser();
          if (data) {
            const rules = convertAbility(data || []);
            ability.update(rules);
          }
        } finally {
          sessionStorage.removeItem("process_create_wizard");
          window.location.replace(getRedirectUrl());
        }
      })
      .catch(() => {
        setIsSwitchInProgress(false);
        sessionStorage.removeItem("process_create_wizard");
        // If update fails, still navigate to force a fresh session state
        window.location.replace(getRedirectUrl());
      });
  };

  const renderOnlineFlag = useCallback(() => {
    return (
      isCounsellor && (
        <>
          <div
            data-navigation-guide={PROFILE_GUIDE_STEPS.AVAILABILITY}
            onClick={() => {
              handleOnline(!isOnline);
            }}
            className="w-full my-2 py-5 px-5 rounded-md text-lg text-dawn-gray tracking-tight hover:bg-pampas hover:bg-opacity-50 cursor-pointer"
          >
            <div className="flex flex-row items-center justify-between w-full">
              <span>{t("navbar.switch")}</span>
              <span
                className={clsx(!isOnline ? "text-jungle-green" : "text-stone")}
              >
                {isOnline ? t("navbar.toOffline") : t("navbar.toOnline")}
              </span>
            </div>
          </div>
          <Divider className="mb-5" />
        </>
      )
    );
  }, [isOnline]);

  return (
    <div
      className={clsx(styles.nav, isStudent && styles.studentNav, className)}
    >
      <NavigationBlockerDialog
        bodyTemplate={t("common.goToAccountConfirmMessage", {
          name: showNavigationConfirmDialog.text,
        })}
        positiveButtonLabel={t("common.yesSwitch")}
        showConfirmDialog={showNavigationConfirmDialog.isOpen}
        handleCloseEvent={() =>
          setShowNavigationConfirmDialog({
            isOpen: false,
            ids: null,
            text: null,
          })
        }
        handleYesEvent={() => {
          if (isAllbryCounsellor) {
            onSwitchAccount(showNavigationConfirmDialog.ids);
          } else {
            handleAccountSwitch(showNavigationConfirmDialog.ids);
          }
        }}
      />
      <div
        className={clsx(
          "flex-shrink-0",
          isStudent ? "w-auto md:w-full md:pt-2" : "w-full",
          !isStudent && (isAllbryCounsellor ? "pt-1" : "pt-2"),
        )}
      >
        {distributedAccounts?.filter((item) => item?.id !== null)?.length >
        1 ? (
          <Popup
            variant="accounts"
            position={isMobileNav ? ["top left"] : ["right"]}
            trigger={SwitchAccountTrigger}
            onOpen={handleDistributedOpen}
          >
            <>{renderAllbryAccounts}</>
          </Popup>
        ) : usersAccounts &&
          usersAccounts?.filter((item) => item?.account?.id !== null)?.length >
            1 ? (
          <Popup
            variant="accounts"
            position={isMobileNav ? ["top left"] : ["right"]}
            trigger={SwitchAccountTrigger}
            onOpen={handleUserOpen}
          >
            <>{renderUsersAccounts}</>
          </Popup>
        ) : (
          <BaseLink
            to={APPOINTMENTS}
            className={clsx(
              "rounded-md items-center relative justify-center mx-3 py-1 cursor-pointer mb-2",
              isStudent ? "hidden md:flex" : "flex",
            )}
          >
            <Logo size="h-10 w-10" />
          </BaseLink>
        )}
      </div>
      <div className={clsx("w-full", styles.navbarList)}>
        <NavbarList
          navItems={navItems}
          unansweredEvent={currentAccountUnansweredEvent}
        />
      </div>
      <div
        className={clsx(
          "flex-shrink-0",
          isStudent
            ? clsx(
                "px-3 py-1 md:px-0 md:pb-4 md:pt-2",
                styles.studentProfileSlot,
              )
            : "pb-4 pt-2",
        )}
      >
        <NavbarProfileMenu
          trigger={PopupTrigger}
          onlineFlag={renderOnlineFlag()}
          isCounsellor={isCounsellor}
          showNotifications={showNotifications}
          htmlFontSize={htmlFontSize}
          profileId={profile.id}
          unreadCount={unreadCount}
          isMobileNav={isMobileNav}
          mobileMenuTextClass={mobileMenuTextClass}
          onLogout={() => {
            logout();
            sendNotify(t("auth.logout.logoutSuccess"), "success");
          }}
        />
      </div>
    </div>
  );
};

export default Navbar;
